import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { formatIDR } from "@kanata/shared";

import { catalog, type ApiProduct } from "@/lib/api";
import { useCart } from "@/lib/cart";
import {
  initialSelection,
  inquirySelections,
  labelFor,
  matches,
  selectedLabel,
  type Selection,
} from "@/lib/cms-variants";
import { INQUIRY_CTA, inquiryWhatsappUrl } from "@/lib/pdp-inquiry";
import { galleryForSelection } from "@/lib/product-images";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { OptionSection } from "@/components/product/OptionSection";
import { PackageContents } from "@/components/product/PackageContents";
import { PriceBlock } from "@/components/product/PriceBlock";
import { ProductGallery } from "@/components/product/ProductGallery";
import { SizePicker } from "@/components/product/SizePicker";
import { SwatchPicker } from "@/components/product/SwatchPicker";

/**
 * Pick a variant and add it without leaving the grid (DEL-434).
 *
 * The product page's configurator, cut down to what a card needs: the photos
 * for the colour chosen, the option axes, the price, one button. Add-ons and
 * pasang stay on the product page, behind "Lihat detail". They are decisions, and
 * a sheet opened from a grid is for people who have already decided.
 *
 * The product is fetched fresh when the sheet opens rather than taken from
 * the grid. The list is cached for the page's life, and stock moves.
 */
export function QuickAddSheet({
  slug,
  name,
  open,
  onOpenChange,
}: {
  slug: string;
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { add, busy } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ApiProduct | null>(null);
  const [error, setError] = useState("");
  const [selection, setSelection] = useState<Selection>({});
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setSlide(0);
    catalog
      .detail(slug)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
        setSelection(initialSelection(p));
      })
      .catch(() => {
        if (!cancelled) setError("Produk gagal dimuat. Silakan coba lagi.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug]);

  const axes = (product?.axes ?? []).filter((a) => !a.notApplicable && a.valueIds.length > 0);
  const skus = product?.skus ?? [];
  const selected = skus.find((s) => s.isActive && matches(s, selection));
  const inquiries = inquirySelections(axes, selection);
  const isInquiry = inquiries.length > 0;
  const gallery = product
    ? galleryForSelection(
        product,
        selected,
        labelFor(axes, "top", selection),
        labelFor(axes, "leg", selection),
      )
    : [];
  const canBuy =
    Boolean(selected) && !selected?.soldOut && !selected?.priceInvalid && !busy && !isInquiry;
  const label = busy
    ? "Menambahkan…"
    : selected?.soldOut
      ? "Stok habis"
      : selected
        ? `Tambah ke keranjang · ${formatIDR(selected.priceIdr)}`
        : "Pilih varian";

  function choose(axisId: string, valueId: string) {
    setSelection((s) => ({ ...s, [axisId]: valueId }));
    setSlide(0);
  }

  async function buy() {
    if (!selected) return;
    setError("");
    try {
      await add(selected.id, 1);
      // Straight to the cart, like the product page. Closed first, so a sheet
      // opened from the cart's own suggestions doesn't stay open over it.
      onOpenChange(false);
      navigate("/keranjang");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal menambahkan produk");
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="overflow-y-auto"
        // Lenis drives the page's wheel scrolling; without this the sheet
        // can't be scrolled with a mouse wheel.
        data-lenis-prevent
        // Radix wants a description or an explicit "none".
        {...(product?.tagline ? {} : { "aria-describedby": undefined })}
      >
        <div className="flex min-h-full flex-col gap-6 px-6 pt-14">
          <div>
            <SheetTitle className="text-2xl font-medium tracking-tight">
              {product?.name ?? name}
            </SheetTitle>
            {product?.tagline && (
              <SheetDescription className="mt-1 text-ink/65">{product.tagline}</SheetDescription>
            )}
          </div>

          {!product && !error && (
            <div className="space-y-4" role="status" aria-label="Memuat produk">
              <Skeleton className="aspect-[4/3] w-full rounded-2xl" />
              <Skeleton className="h-8 w-1/2" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {product && (
            <>
              <ProductGallery
                images={gallery}
                index={slide}
                onIndex={setSlide}
                name={product.name}
                stacked
              />

              <PriceBlock
                selected={selected}
                addonsTotal={0}
                total={selected?.priceIdr}
                isInquiry={isInquiry}
              />
              {selected?.isPackage && <PackageContents sku={selected} />}

              {axes.length > 0 && (
                <div className="space-y-6">
                  {axes.map((a, i) => {
                    const headingId = `quick-axis-${product.id}-${a.optionTypeId}`;
                    const Picker = a.optionType.control === "swatch" ? SwatchPicker : SizePicker;
                    return (
                      <OptionSection
                        key={a.optionTypeId}
                        n={i + 1}
                        id={headingId}
                        title={a.optionType.label}
                        selectedLabel={selectedLabel(a, selection)}
                      >
                        <Picker
                          axis={a}
                          skus={skus}
                          selection={selection}
                          onChange={(v) => choose(a.optionTypeId, v)}
                          labelledBy={headingId}
                        />
                      </OptionSection>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Pinned, so the button is on screen whatever the axes' height. */}
          <div className="sticky bottom-0 -mx-6 mt-auto space-y-3 border-t border-ink/8 bg-white px-6 pt-5 pb-6">
            {product && isInquiry ? (
              <>
                <p className="text-sm text-ink/70">{INQUIRY_CTA}.</p>
                <Button size="lg" className="w-full" asChild>
                  <a
                    href={inquiryWhatsappUrl(product.name, inquiries)}
                    data-wa-placement="quick_add_inquiry"
                    data-product-slug={product.slug}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle />
                    Chat Admin
                  </a>
                </Button>
              </>
            ) : (
              <Button variant="cart" size="lg" className="w-full" disabled={!canBuy} onClick={() => void buy()}>
                {label}
              </Button>
            )}
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button variant="outline" size="lg" className="w-full" asChild>
              <Link to={`/produk/${slug}`} onClick={() => onOpenChange(false)}>
                Lihat detail
              </Link>
            </Button>
            {(product?.pasangEligible || Boolean(product?.addons?.length)) && (
              <p className="text-center text-xs text-ink/50">Aksesori tambahan dan pemasangan ada di halaman detail.</p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
